import { EventSourceParserStream } from 'eventsource-parser/stream';
import { chatUrl } from '../config';
import type {
  ChatErrorCode,
  ChatRequest,
  ChatStreamEvent,
} from '../types/types.ts';

type StreamChatOptions = {
  request: ChatRequest;
  getAccessToken: () => Promise<string | undefined>;
  onEvent: (event: ChatStreamEvent) => void;
  signal?: AbortSignal;
};

export async function streamChatResponse(
  options: StreamChatOptions,
): Promise<void> {
  const { onEvent, signal } = options;
  const sendError = (code: ChatErrorCode) =>
    onEvent({ type: 'error', error: { code } });
  try {
    const response = await sendQuestionRequest(options);
    // string als response ist immer error
    if (typeof response === 'string') {
      sendError(response);
      return;
    }
    const finished = await readEvents(response, onEvent);
    if (!finished) {
      sendError('STREAM_INTERRUPTED');
    }
  } catch {
    if (!signal?.aborted) {
      sendError('NETWORK_ERROR');
    }
  }
}

async function sendQuestionRequest({
  request,
  getAccessToken,
  signal,
}: StreamChatOptions): Promise<
  ReadableStream<Uint8Array<ArrayBuffer>> | ChatErrorCode
> {
  const token = await getAccessToken();
  if (!token) {
    return 'UNAUTHORIZED';
  }
  const response = await fetch(chatUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(request),
    signal,
  });

  if (response.ok && response.body) {
    return response.body;
  }
  return toErrorCode(response);
}

async function readEvents(
  body: ReadableStream<Uint8Array<ArrayBuffer>>,
  onEvent: (event: ChatStreamEvent) => void,
): Promise<boolean> {
  const reader = body
    .pipeThrough(new TextDecoderStream())
    .pipeThrough(new EventSourceParserStream())
    .getReader();

  let next = await reader.read();
  while (!next.done) {
    const event = JSON.parse(next.value.data) as ChatStreamEvent;
    onEvent(event);
    if (event.type === 'done' || event.type === 'error') {
      await reader.cancel();
      return true;
    }
    next = await reader.read();
  }
  return false;
}

async function toErrorCode(response: Response): Promise<ChatErrorCode> {
  if (response.status === 401) {
    return 'UNAUTHORIZED';
  }
  if (response.status === 400) {
    return 'INVALID_REQUEST';
  }

  if (
    response.status === 404 &&
    (await readErrorCode(response)) === 'SESSION_NOT_FOUND'
  ) {
    return 'SESSION_NOT_FOUND';
  }
  return 'HTTP_ERROR';
}

async function readErrorCode(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.json()) as { code?: unknown };
    return typeof body.code === 'string' ? body.code : undefined;
  } catch {
    return undefined;
  }
}
